import { SectionLabel } from './RoundTimeline'

// Written for players and the coach, not for whoever maintains this. The priority is the numbers
// that are easy to MISREAD — a stat a coach trusts wrongly is worse than one we never shipped.

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const

export function Help() {
  return (
    <div className="space-y-5">
      <section className="border border-border bg-surface p-6" style={card}>
        <SectionLabel n="H0" label="What this is" />
        <p className="mt-3 text-sm leading-relaxed text-text-dim">
          A private review and scouting tool for JANO. Upload a demo and it gets parsed into a
          scoreboard, deep stats, a 2D replay and an AI review. It also pulls our fixtures from
          FACEIT, so it knows who we play next and prepares their scouting before anyone asks.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <Tab name="Matches" what="One game. Scoreboard, stats, 2D replay, AI review. Upload demos here." />
          <Tab name="Season" what="Us over time. Player form, per-map trends, our own tendencies — the questions one match can't answer." />
          <Tab name="Players / My page" what="One of us over time: form match by match, maps, and every note the reviews have given. Players see their own and land here by default; coaches see the whole squad." />
          <Tab name="Find" what="Query rounds. Pick a situation — map, side, buy, man-down, bomb — across our games or an opponent's, and jump straight to each in the replay." />
          <Tab name="Scouting" what="Them. One folder per opponent: veto sheet, tendencies, utility map, AI report." />
          <Tab name="Find" what="Rounds by situation, and a search across every note anyone has written. The place for 'what did we say about their B split last time'." />
          <Tab name="Notes" what="On every match, beside the AI review. Write a note against a round and it opens the replay there; coaches can assign one to somebody and it turns up on their page. Notes can be edited, and the replayer's pen drawings are saved with the match." />
        </div>
      </section>

      <section className="border border-border bg-surface p-6" style={card}>
        <SectionLabel n="H1" label="The week" />
        <ol className="mt-3 space-y-2 text-sm text-text-dim">
          <Step n="Before the match">
            Open <b>Matches → Next up</b>, hit <b>scout them</b>. The <b>map veto</b> sheet is at the
            top of their folder — it works even for a team we've never recorded, because it runs on
            FACEIT's record rather than on demos.
          </Step>
          <Step n="During the match">
            Coach: type <code>!tac</code> in chat when you call a timeout. It costs nothing and it's
            the difference between the portal knowing who called it and guessing.
          </Step>
          <Step n="After the match">
            Drop the demo on <b>Matches</b>. It parses in ~1–2 min, files itself into the opponent's
            scouting folder automatically, and the AI review appears on the match page. If it was a
            walkover there is no demo to drop — mark the fixture <b>ffw</b> or <b>ffl</b> on its row
            under <b>Loose ends</b>, the list below Next up that holds matches already played and
            still owing something. It stops asking for a demo, and the row clears itself two days
            later (it stays that long so a mis-click can be undone from the same row).
          </Step>
          <Step n="Reviewing">
            In the <b>2D replay</b>, use <b>Moments</b> instead of scrubbing — jump straight to our
            opening deaths, rounds after a timeout, eco rounds. The <b>✎ draw</b> tool marks up the
            map, and <b>● clip</b> saves a video of the round with your drawing in it.
          </Step>
        </ol>
        <p className="mt-3 text-xs text-text-faint">
          Scouting demos (opponent games we're not in) go on the <b>Scouting</b> page instead — they
          stay out of Matches so our own record isn't polluted. One demo of two other teams files
          into <em>both</em> their folders.
        </p>
      </section>

      <section className="border border-border bg-surface p-6" style={card}>
        <SectionLabel n="H2" label="Reading the numbers" />
        <p className="mt-2 text-xs text-text-faint">
          These are the ones that are easy to misread. Getting them wrong leads somewhere worse than
          not having them.
        </p>
        <div className="mt-4 space-y-4">
          <Stat
            name="Trades — 10/5/22"
            read="trade kills / our deaths that got traded / deaths nobody traded"
          >
            The last number is the one that matters. A death nobody punishes isn't a lost aim duel,
            it's a broken setup — someone was somewhere no teammate could cover. High untraded with
            low trade kills across the team is a <b>spacing problem, not an aim problem</b>.
          </Stat>

          <Stat name="Rating" read="1.00 is average, above is good">
            This is an <b>approximation</b> of HLTV's Rating 2.0, not HLTV's own number — theirs is
            secret, so nobody can reproduce it exactly. It will disagree with your HLTV or Leetify
            profile. Trust the <b>order</b> (who's carrying, who's struggling) far more than the
            exact value. Measured across our demos it sits within 0.5% of the 1.00 target. It used
            to read low because KAST wasn't counting assists or traded deaths — so if you remember
            your rating being harsh, that was real, and it's fixed.
          </Stat>

          <Stat name="KAST" read="% of rounds you did something">
            A kill, an assist, surviving, or dying traded. It's the consistency number: a player can
            have a mediocre K/D and a high KAST because they're always contributing something. Low
            KAST with a good rating means feast-or-famine.
          </Stat>

          <Stat name="Opening → round" read="when we win/lose first blood, how often we win the round">
            The gap between the two lines is how much the opening decides a round <em>for us</em>.
            If losing the opening means we win almost nothing, the fix isn't "win more duels" — it's
            that we have no answer once we're a man down.
          </Stat>

          <Stat name="Post-plant vs retakes" read="two different problems that look identical in round win%">
            Post-plant is T-side: bomb down, did we close it. Retake is CT-side: bomb down against
            us, did we get it back. A team can be excellent at one and hopeless at the other, and
            round win% alone will never tell you which.
          </Stat>

          <Stat name="Pace to A / B" read="how fast they arrive at the site">
            Median seconds from <em>freeze end</em> (when players can move) until a T reaches the
            site. <b>≤25s is a set play</b> —
            they're running a rehearsed execute. <b>≥40s is a default</b> — they're playing the map
            and taking information first. Different things to prepare for.
          </Stat>

          <Stat name="Timeouts — ✓ and ~" read="who called it">
            <b>✓</b> means someone typed <code>!tac</code> in chat, so we know. <b>~</b> means it was
            called from the in-game menu, which leaves no trace, so we inferred it from who was being
            run over. Detection of the timeout itself is solid either way — normal breaks are exactly
            27s and a timeout is 57s+, with nothing in between.
          </Stat>

          <Stat name="Clutch — 2/10" read="hover it for the 1v1..1v5 split">
            The total on its own is misleading. Winning 1v1s is expected; winning 1v2 or worse is a
            real skill. And a player who keeps appearing in 1v3s and 1v4s isn't necessarily a bad
            clutcher — they're the last one alive in rounds that were already lost, which is the
            same story as high untraded deaths, seen from the other end.
          </Stat>

          <Stat name='"thin" badges' read="not enough games to mean anything">
            Anywhere you see <b className="text-loss">thin</b>, the number is real but the sample
            isn't. One demo on a map is what a team did in <em>one game</em>, not what they do. The
            veto sheet won't recommend a map it doesn't trust, even when the edge looks big.
          </Stat>

          <Stat name="Grenades in the replay" read="you see the throw, not just the smoke">
            A grenade in the air is drawn as a moving dot with a dashed trail back to where it left
            the player's hand. That trail is the <b>lineup</b> — the landing spot tells you a smoke
            was at mid, the trail tells you it came from T spawn, which is the half you can copy or
            punish. Colours match the utility map: grey smoke, orange molotov, red HE, pale flash.
          </Stat>

          <Stat name={'"What we\'ve told you to fix"'} read="on your Players page">
            Every match review writes one line about each of us. On its own that's a note about one
            game. Stacked down the page, a point that keeps <em>reappearing</em> is the one worth a
            practice session — and a point that shows up once is just one game. That's the whole
            reason the page exists; the lines were always there, you just couldn't see them together.
            Everyone can read everyone's, deliberately.
          </Stat>

          <Stat name="Find (round finder)" read="a situation → the rounds where it happened">
            Instead of scrubbing a whole demo, pick filters — map, side, buy type, bomb planted,
            whether we went a man down, who took first blood — and get every matching round as a
            list. Each one opens the 2D replay right at it. Point it at <b>our matches</b> to review
            ("every B retake we lost on Anubis") or at an <b>opponent folder</b> to prep ("their
            full-buy A hits"). It runs on the demos we already have — no pro library.
          </Stat>

          <Stat name="our CT / our T" read="on either heatmap">
            Splits the map by the side <em>we</em> were on that round. It matters because "we die a
            lot on Anubis" isn't a fix and "our B-site CT setup gets run over" is — they're
            different problems, and only one of them tells you what to practise.
          </Stat>

          <Stat name="What the season says" read="AI review of the whole period, not one game">
            The match review answers "how did that game go". This one answers "what keeps
            happening", which is what you plan a practice week from. It's written from the same
            numbers on the Season page — if a claim surprises you, check the table it came from.
          </Stat>

          <Stat name="Utility map" read="every grenade from every demo, at once">
            Bright clusters are the setup they run <em>every</em> round; faint singles are one-offs.
            <b> lineups</b> draws a line from where each grenade was thrown to where it landed, with
            a dot at the throwing end — that dot is a spot you can hold, smoke off, or take away.
            That's the whole point — one demo is a round, seven demos is an execute. Blue lines are
            their T-side routes into the site you've selected, with a dot where each run ended —
            that's the contact point you'd set up against. <b>CT holds</b> adds red dots showing
            where their CTs stand 12 seconds in: an area with few dots is where they're slowest to
            help.
          </Stat>

          <Stat name="upper / lower buttons" read="nuke and train have two floors">
            One radar image can only show one storey, and position alone can't tell them apart — a
            player in lower B would otherwise be drawn on top of one on upper A. Anywhere you see
            these buttons the map is two-level: heatmaps, the utility map and the 2D replay all
            split by floor. The floor boundaries are Valve's own, not our guess. In the{' '}
            <b>replay</b> the switch is manual on purpose — the map flipping under you mid-round
            would be worse — and players on the far floor stay visible, just dimmed and without a
            view cone, so you can see someone drop to ramp and follow them down.
          </Stat>
        </div>
      </section>

      <section className="border border-border bg-surface p-6" style={card}>
        <SectionLabel n="H2b" label="Newer things, and how to read them" />
        <ul className="mt-3 space-y-2 text-sm text-text-dim">
          <li>
            • <b>Debrief</b> (on a match, above the notes). The four steps that turn a parse into
            something anyone acts on: read the review, mark the rounds worth watching, put someone on
            each, tell them. It's a checklist, not a wizard — do them in any order, skip what you
            like. Nothing is disabled by an unfinished box. The last step is the one that gets
            forgotten and the one that makes the other three worth doing.
          </li>
          <li>
            • <b>Playlists</b> (on Find rounds). A named, ordered set of rounds — "their A executes".
            Different from a saved situation on the same page: a <i>situation</i> re-runs a query and
            stays current, a <i>playlist</i> keeps exactly the rounds somebody picked. Order is the
            point, so nothing sorts itself.
          </li>
          <li>
            • <b>Side by side</b> (on Players, coaches). Two players on the same rows. Read the
            highlight as "the better number", not the bigger one — untraded deaths reads the other
            way. Check the demo count before reading much into a gap.
          </li>
          <li>
            • <b>Economy, past the headline</b> (Season and Scouting). Whether forcing converts,
            whether a lost round gets saved or thrown away, and whether a lost eco costs the round
            after it too. Every figure shows how many rounds it came from — a dim number saying "too
            few to trust" means exactly that, and "—" means it never happened, which is not 0%.
          </li>
          <li>
            • <b>Slides nobody stopped</b> (Season). Three or more rounds lost in a row with no
            timeout called. The timeout numbers above it can only see the timeouts that happened;
            this is the other half.
          </li>
          <li>
            • <b>Next session</b> (Season). The practice plan, assembled from everything above. It is
            deliberately short and never padded — if it lists two things, two things is what the
            season supports. Every line names the number behind it so you can disagree with it.
          </li>
          <li>
            • <b>What changed between two demos</b> (a scouting folder). The folder averages every
            demo you hold, which is what hides a change — a team that stopped hitting B looks like a
            team that half-does both. This subtracts one demo from another instead.
          </li>
          <li>
            • <b>Rotation timings.</b> After contact starts on a site, how long the off-site CTs take
            to arrive. First kill stands in for first contact, so a duel where someone takes damage
            and lives isn't counted. Nuke is left out — its sites are stacked, and a flat answer
            would be wrong rather than approximate.
          </li>
        </ul>
      </section>

      <section className="border border-border bg-surface p-6" style={card}>
        <SectionLabel n="H2a" label="Things that come to you" />
        <ul className="mt-3 space-y-2 text-sm text-text-dim">
          <li>
            • <b>A demo finishing.</b> Discord gets the result, the map, a link, our round record on
            each side, and our top three by rating — enough to know how it went without opening
            anything. Uploading an opponent's demo on the <b>Scouting</b> page posts nothing: nobody
            in the channel played in it, and a scouting session is a dozen demos in one sitting.
          </li>
          <li>
            • <b>The match-day brief.</b> At 18:00 on the day of a match, Discord gets a short brief:
            their five from the last demo we hold, a warning if those demos are actually different
            rosters, the key read from the scouting report, ban/pick, and our last result. A daytime
            fixture gets it at 09:00 instead. Coaches can see the wording early — and send it early —
            from the Matches page.
          </li>
          <li>
            • <b>"Since the last match"</b> on the Season tab: what moved in the most recent game
            against the average of everything before it. Not against the previous match, which would
            make every result look like a swing.
          </li>
          <li>
            • <b>Your page leads with what's aimed at you</b> — assignments first, then mentions.
            Clear a mention when you've read it; an assignment goes away when you mark it done.
          </li>
          <li>
            • <b>Repeated feedback rises to the top.</b> If the AI has told you the same thing in
            three matches, it's grouped and counted rather than left for you to notice.
          </li>
        </ul>
      </section>

      <section className="border border-border bg-surface p-5" style={card}>
        <SectionLabel n="H2b" label="Signing in, and who sees what" />
        <ul className="mt-3 space-y-2 text-sm text-text-dim">
          <li>
            • <b>Sign in through Steam.</b> Only accounts on the team roster can get in — if it turns
            you away, ask your coach to add you. Your Steam account is also how the portal knows
            which lines on a scoreboard are yours, so <b>Players</b> can show you your own season.
          </li>
          <li>
            • <b>Three roles.</b> <b>Players</b> read everything the team can see and upload demos.
            <b> Coaches</b> also regenerate AI reports and delete demos and scouting folders.
            <b> Admins</b> also manage the roster. Buttons you don't have the role for aren't shown.
          </li>
          <li>
            • <b>Your feedback is yours.</b> On a match's AI review a player sees their own
            assessment; coaches see all five. Numbers — scoreboard, season table, form — are the
            team's and everyone sees them whole.
          </li>
          <li>
            • <b>Changed your in-game name?</b> Nothing breaks. Everything is tracked by Steam
            account, so a rename is still one player with one season — the old names show up as
            <i> aka</i> under the new one. An admin can also set a fixed nickname on the roster, and
            that's what the portal will call you everywhere.
          </li>
        </ul>
      </section>

      <section className="border border-border bg-surface p-5" style={card}>
        <SectionLabel n="H3" label="What it can't do" />
        <ul className="mt-3 space-y-2 text-sm text-text-dim">
          <li>
            • <b>Small samples.</b> We get 1–2 league games a week, so most per-map numbers rest on a
            handful of rounds all season. The portal labels this rather than hiding it — believe the
            labels.
          </li>
          <li>
            • <b>The AI report is a reading, not a fact.</b> It's generated from the same numbers you
            can see, and it hedges when the sample is small. If it says something surprising, check
            the table it came from.
          </li>
          <li>
            • <b>A scouting folder can be more than one team.</b> Seven demos of an opponent might be
            three different fives. The folder now says so — each demo shows how much of its lineup is
            still in their latest one — but every aggregate above that is still the blend. Read a
            mixed folder as "this org", not "this team".
          </li>
          <li>
            • <b>The veto sheet is win-rate only.</b> FACEIT gives us maps won and lost, not rounds —
            so a 13–11 and a 13–2 count the same.
          </li>
          <li>
            • <b>Nothing here is private from the coaches.</b> They see every player's numbers and
            every AI note. What's private is sideways: your teammates can't read the AI's feedback
            about you, and you can't read theirs.
          </li>
          <li>
            • <b>AI reports are cached and capped.</b> A report only regenerates when its data
            changes or you press <b>regenerate</b> — so browsing costs nothing. There's a monthly
            token ceiling as a runaway guard (normal use is a fraction of a percent of it); if it
            were ever hit, generation refuses with a clear message rather than running up a bill.
          </li>
        </ul>
      </section>
    </div>
  )
}

function Tab({ name, what }: { name: string; what: string }) {
  return (
    <div className="border border-border bg-bg-2 p-3" style={{ borderRadius: 'var(--radius)' }}>
      <div className="font-display text-sm uppercase tracking-wider text-accent">{name}</div>
      <div className="mt-1 text-xs leading-relaxed text-text-dim">{what}</div>
    </div>
  )
}

function Step({ n, children }: { n: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="font-display w-32 shrink-0 text-xs uppercase tracking-wider text-accent">{n}</span>
      <span className="leading-relaxed">{children}</span>
    </li>
  )
}

function Stat({ name, read, children }: { name: string; read: string; children: React.ReactNode }) {
  return (
    <div className="border-l-2 border-border pl-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-display text-sm text-text">{name}</span>
        <span className="text-[11px] text-text-faint">{read}</span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-text-dim">{children}</p>
    </div>
  )
}
